import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, ArrowDownLeft, ArrowUpRight, Check, X, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatPrice, formatPriceFixed, formatRupeeFixed } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'UPI'];

const Section = ({ title, count, subtotal, accent, children, testId }) => (
  <div data-testid={testId} className="mb-3 last:mb-0">
    <div className={`flex items-center justify-between px-3 py-1.5 rounded-t border-l-2 ${accent} bg-white border-t border-r border-stone-200`}>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-700">{title} <span className="text-stone-400 font-normal normal-case">&middot; {count} {count === 1 ? 'entry' : 'entries'}</span></span>
      <span className="text-xs font-bold tabular-nums text-stone-900">{formatRupeeFixed(subtotal)}</span>
    </div>
    <div className="bg-white border border-stone-200 rounded-b">{children}</div>
  </div>
);

const VendorStatement = ({ statement }) => {
  const fmt = formatPriceFixed;
  const co = statement.customer_orders || [];
  const po = statement.purchase_orders || [];
  const ec = statement.extra_charges || [];
  const pm = statement.payments_made || [];
  return (
    <div className="p-4 border-l-2 border-amber-700 bg-amber-50/30">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-xs font-bold text-stone-800">Vendor Statement &mdash; {statement.vendor_name}</h4>
        <div className="flex gap-4 text-[11px]">
          <span className="text-stone-600">Total Owed: <strong className="text-stone-900 tabular-nums">&#8377;{fmt(statement.total_owed)}</strong></span>
          <span className="text-stone-600">Paid: <strong className="text-lime-700 tabular-nums">&#8377;{fmt(statement.payments_subtotal)}</strong></span>
          <span className="text-stone-600">Outstanding: <strong className={`tabular-nums ${statement.outstanding > 0 ? 'text-red-700' : 'text-lime-700'}`}>&#8377;{fmt(statement.outstanding)}</strong></span>
        </div>
      </div>

      {/* A. Customer Orders */}
      <Section title="A. Customer Orders (received from vendor)" count={co.length} subtotal={statement.customer_orders_subtotal} accent="border-blue-500" testId="stmt-customer-orders">
        {co.length === 0 ? <div className="p-3 text-[11px] text-stone-500 text-center">No customer-order items received from this vendor.</div> : (
          <table className="w-full text-[11px]">
            <thead className="bg-stone-50">
              <tr className="border-b border-stone-200">
                <th className="text-left p-2 font-semibold text-stone-600">DATE</th>
                <th className="text-left p-2 font-semibold text-stone-600">ORDER</th>
                <th className="text-left p-2 font-semibold text-stone-600">CUSTOMER &middot; PROJECT</th>
                <th className="text-left p-2 font-semibold text-stone-600">PRODUCT</th>
                <th className="text-right p-2 font-semibold text-stone-600">QTY</th>
                <th className="text-right p-2 font-semibold text-stone-600">UNIT &#8377;</th>
                <th className="text-right p-2 font-semibold text-stone-600">LINE TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {co.map((r) => (
                <tr key={r.order_item_id} className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50">
                  <td className="p-2 tabular-nums text-stone-600">{r.order_date}</td>
                  <td className="p-2 font-mono text-stone-700">{r.order_id}</td>
                  <td className="p-2 text-stone-700">{r.customer_name} <span className="text-stone-400">&middot;</span> <span className="text-stone-500">{r.project_name}</span></td>
                  <td className="p-2 text-stone-700">{r.product_name}</td>
                  <td className="p-2 text-right tabular-nums text-stone-700">{Number(r.received_qty).toFixed(0)}</td>
                  <td className="p-2 text-right tabular-nums text-stone-700">{fmt(r.unit_input_price)}</td>
                  <td className="p-2 text-right tabular-nums font-semibold text-stone-900">{fmt(r.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* B. Purchase Orders */}
      <Section title="B. Purchase Orders (direct restock)" count={po.length} subtotal={statement.purchase_orders_subtotal} accent="border-violet-500" testId="stmt-purchase-orders">
        {po.length === 0 ? <div className="p-3 text-[11px] text-stone-500 text-center">No purchase-order items received from this vendor.</div> : (
          <table className="w-full text-[11px]">
            <thead className="bg-stone-50">
              <tr className="border-b border-stone-200">
                <th className="text-left p-2 font-semibold text-stone-600">DATE</th>
                <th className="text-left p-2 font-semibold text-stone-600">PO #</th>
                <th className="text-left p-2 font-semibold text-stone-600">PRODUCT</th>
                <th className="text-right p-2 font-semibold text-stone-600">QTY</th>
                <th className="text-right p-2 font-semibold text-stone-600">UNIT &#8377;</th>
                <th className="text-right p-2 font-semibold text-stone-600">LINE TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {po.map((r) => (
                <tr key={r.po_item_id} className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50">
                  <td className="p-2 tabular-nums text-stone-600">{r.po_date}</td>
                  <td className="p-2 font-mono text-stone-700">{r.purchase_order_id}</td>
                  <td className="p-2 text-stone-700">{r.product_name}</td>
                  <td className="p-2 text-right tabular-nums text-stone-700">{Number(r.received_qty).toFixed(0)}</td>
                  <td className="p-2 text-right tabular-nums text-stone-700">{fmt(r.unit_input_price)}</td>
                  <td className="p-2 text-right tabular-nums font-semibold text-stone-900">{fmt(r.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* C. Extra Charges */}
      <Section title="C. Extra Charges (approved requests)" count={ec.length} subtotal={statement.extra_charges_subtotal} accent="border-amber-500" testId="stmt-extra-charges">
        {ec.length === 0 ? <div className="p-3 text-[11px] text-stone-500 text-center">No approved extra charges.</div> : (
          <table className="w-full text-[11px]">
            <thead className="bg-stone-50">
              <tr className="border-b border-stone-200">
                <th className="text-left p-2 font-semibold text-stone-600">DATE</th>
                <th className="text-left p-2 font-semibold text-stone-600">DESCRIPTION</th>
                <th className="text-right p-2 font-semibold text-stone-600">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {ec.map((r) => (
                <tr key={r.request_id} className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50">
                  <td className="p-2 tabular-nums text-stone-600">{r.date}</td>
                  <td className="p-2 text-stone-700">{r.description}</td>
                  <td className="p-2 text-right tabular-nums font-semibold text-stone-900">{fmt(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* D. Payments Made */}
      <Section title="D. Payments Made" count={pm.length} subtotal={statement.payments_subtotal} accent="border-lime-500" testId="stmt-payments-made">
        {pm.length === 0 ? <div className="p-3 text-[11px] text-stone-500 text-center">No payments recorded yet.</div> : (
          <table className="w-full text-[11px]">
            <thead className="bg-stone-50">
              <tr className="border-b border-stone-200">
                <th className="text-left p-2 font-semibold text-stone-600">DATE</th>
                <th className="text-left p-2 font-semibold text-stone-600">METHOD</th>
                <th className="text-left p-2 font-semibold text-stone-600">REF #</th>
                <th className="text-left p-2 font-semibold text-stone-600">NOTES</th>
                <th className="text-right p-2 font-semibold text-stone-600">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {pm.map((r) => (
                <tr key={r.payment_id} className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50">
                  <td className="p-2 tabular-nums text-stone-600">{r.date}</td>
                  <td className="p-2 text-stone-700">{r.payment_method}</td>
                  <td className="p-2 font-mono text-stone-600">{r.reference_number || '-'}</td>
                  <td className="p-2 text-stone-600">{r.notes || '-'}</td>
                  <td className="p-2 text-right tabular-nums font-semibold text-lime-700">{fmt(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <div className="mt-2 px-3 py-2 bg-stone-100 border border-stone-200 rounded text-[11px] flex items-center justify-between">
        <span className="text-stone-600">Total Owed = A + B + C = &#8377;{fmt(statement.customer_orders_subtotal)} + &#8377;{fmt(statement.purchase_orders_subtotal)} + &#8377;{fmt(statement.extra_charges_subtotal)}</span>
        <span className="font-bold text-stone-900">&#8377;{fmt(statement.total_owed)}</span>
      </div>
    </div>
  );
};

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [payables, setPayables] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cash_in');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [methodFilter, setMethodFilter] = useState('ALL');
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [vendorStatements, setVendorStatements] = useState({});
  const [statementLoading, setStatementLoading] = useState(null);
  const [formData, setFormData] = useState({
    type: 'cash_in',
    entity_type: 'customer',
    entity_id: '',
    amount: '',
    discount: '',
    round_off: '',
    payment_method: '',
    reference_number: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [paymentsRes, custRes, vendRes, recRes, payRes, reqRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/payments`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/customers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/vendors`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/financial/receivables`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/financial/payables`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/vendor/payment-requests`, { withCredentials: true })
      ]);
      setPayments(paymentsRes.data);
      setCustomers(custRes.data);
      setVendors(vendRes.data);
      setReceivables(recRes.data);
      setPayables(payRes.data);
      setPaymentRequests(reqRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type) => {
    setFormData({
      type,
      entity_type: type === 'cash_in' ? 'customer' : 'vendor',
      entity_id: '',
      amount: '',
      discount: '',
      round_off: '',
      payment_method: '',
      reference_number: '',
      notes: '',
      date: new Date().toISOString().split('T')[0]
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.entity_id || !formData.amount || !formData.payment_method) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/payments`, {
        ...formData,
        amount: parseFloat(formData.amount),
        discount: formData.discount ? parseFloat(formData.discount) : 0,
        round_off: formData.round_off ? parseFloat(formData.round_off) : 0
      }, { withCredentials: true });
      toast.success(formData.type === 'cash_in' ? 'Payment received' : 'Payment recorded');
      setDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/payments/${paymentId}`, { withCredentials: true });
      toast.success('Payment deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const handlePaymentRequest = async (requestId, action) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/vendor/payment-requests/${requestId}`, { status: action }, { withCredentials: true });
      toast.success(action === 'Approved' ? 'Approved & payment recorded' : 'Request rejected');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const getEntityName = (payment) => {
    if (payment.entity_type === 'customer') {
      return customers.find(c => c.customer_id === payment.entity_id)?.name || payment.entity_id;
    }
    return vendors.find(v => v.vendor_id === payment.entity_id)?.vendor_name || payment.entity_id;
  };

  const methodBadge = (method) => {
    const colors = {
      'Cash': 'bg-lime-100 text-lime-800',
      'Bank Transfer': 'bg-blue-100 text-blue-800',
      'UPI': 'bg-violet-100 text-violet-800'
    };
    return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[method] || 'bg-stone-100 text-stone-800'}`}>{method}</span>;
  };

  const tabPayments = payments.filter(p => p.type === activeTab);
  const filteredPayments = methodFilter === 'ALL' ? tabPayments : tabPayments.filter(p => p.payment_method === methodFilter);
  const tabTotal = filteredPayments.reduce((s, p) => s + p.amount, 0);

  const toggleVendorStatement = async (vendorId) => {
    if (expandedVendor === vendorId) {
      setExpandedVendor(null);
      return;
    }
    setExpandedVendor(vendorId);
    if (vendorStatements[vendorId]) return;
    setStatementLoading(vendorId);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/financial/payables/${vendorId}/statement`, { withCredentials: true });
      setVendorStatements(prev => ({ ...prev, [vendorId]: res.data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load vendor statement');
      setExpandedVendor(null);
    } finally {
      setStatementLoading(null);
    }
  };

  const outstandingList = activeTab === 'cash_in' ? receivables : payables;
  const totalOutstanding = outstandingList.reduce((s, r) => s + r.outstanding, 0);

  if (loading) return <div className="text-stone-600">Loading payments...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Payments</h1>
          <p className="mt-1 text-xs text-stone-600">Track money in & out</p>
        </div>
        <Button data-testid="record-payment-btn" onClick={() => openDialog(activeTab)} size="sm" className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-8">
          <Plus className="w-3 h-3 mr-1" /> {activeTab === 'cash_in' ? 'Record Cash-In' : 'Record Cash-Out'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-stone-100 p-1 rounded-lg w-fit">
        <button
          data-testid="tab-cash-in"
          onClick={() => setActiveTab('cash_in')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold transition-colors ${activeTab === 'cash_in' ? 'bg-white text-lime-800 shadow-sm' : 'text-stone-600 hover:text-stone-800'}`}
        >
          <ArrowDownLeft className="w-3.5 h-3.5" /> Cash-In (from Customers)
        </button>
        <button
          data-testid="tab-cash-out"
          onClick={() => setActiveTab('cash_out')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold transition-colors ${activeTab === 'cash_out' ? 'bg-white text-red-800 shadow-sm' : 'text-stone-600 hover:text-stone-800'}`}
        >
          <ArrowUpRight className="w-3.5 h-3.5" /> Cash-Out (to Vendors)
        </button>
      </div>

      {/* Vendor Payment Requests (Admin view — only on cash_out tab) */}
      {activeTab === 'cash_out' && paymentRequests.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-lg overflow-hidden">
          <div className="p-3 border-b border-amber-200 bg-amber-50">
            <h3 className="text-xs font-semibold text-amber-900">Vendor Payment Requests &middot; {paymentRequests.filter(r => r.status === 'Pending').length} pending</h3>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-amber-50 border-b border-amber-200">
              <tr>
                <th className="text-left p-2.5 font-semibold text-amber-800">VENDOR</th>
                <th className="text-left p-2.5 font-semibold text-amber-800">DATE</th>
                <th className="text-left p-2.5 font-semibold text-amber-800">DESCRIPTION</th>
                <th className="text-right p-2.5 font-semibold text-amber-800">AMOUNT</th>
                <th className="text-center p-2.5 font-semibold text-amber-800">STATUS</th>
                <th className="text-right p-2.5 font-semibold text-amber-800 w-24">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {paymentRequests.map((pr) => (
                <tr key={pr.request_id} data-testid={`admin-pay-req-${pr.request_id}`} className="border-b border-amber-100 hover:bg-amber-50/50">
                  <td className="p-2.5 font-medium text-stone-900">{pr.vendor_name}</td>
                  <td className="p-2.5 tabular-nums text-stone-700">{pr.date}</td>
                  <td className="p-2.5 text-stone-700">{pr.description}</td>
                  <td className="p-2.5 text-right font-semibold tabular-nums text-stone-900">&#8377;{formatPrice(pr.amount)}</td>
                  <td className="p-2.5 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      pr.status === 'Approved' ? 'bg-lime-100 text-lime-800' :
                      pr.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                      pr.status === 'Cancelled' ? 'bg-stone-100 text-stone-600' :
                      'bg-amber-100 text-amber-800'
                    }`}>{pr.status}</span>
                  </td>
                  <td className="p-2.5 text-right">
                    {pr.status === 'Pending' ? (
                      <div className="flex gap-1 justify-end">
                        <button
                          data-testid={`approve-req-${pr.request_id}`}
                          onClick={() => handlePaymentRequest(pr.request_id, 'Approved')}
                          className="inline-flex items-center gap-0.5 px-2 py-1 rounded text-xs font-semibold bg-lime-100 text-lime-800 border border-lime-200 hover:bg-lime-200 transition-colors"
                        >
                          <Check className="w-3 h-3" /> Approve
                        </button>
                        <button
                          data-testid={`reject-req-${pr.request_id}`}
                          onClick={() => handlePaymentRequest(pr.request_id, 'Rejected')}
                          className="inline-flex items-center gap-0.5 px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-200 hover:bg-red-200 transition-colors"
                        >
                          <X className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-stone-400">{pr.resolved_at?.split('T')[0] || ''}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Outstanding Summary */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-stone-200 bg-stone-50">
          <h3 className="text-xs font-semibold text-stone-700">{activeTab === 'cash_in' ? 'Pending from Clients' : 'Pending to Vendors'} &middot; Total Outstanding: <span className={totalOutstanding > 0 ? 'text-red-700' : 'text-lime-700'}>&#8377;{formatPrice(totalOutstanding)}</span></h3>
        </div>
        {outstandingList.length === 0 ? (
          <div className="p-6 text-center text-stone-500 text-xs">No outstanding amounts</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-stone-50">
              <tr className="border-b border-stone-200">
                <th className="text-left p-2.5 font-semibold text-stone-600">{activeTab === 'cash_in' ? 'CUSTOMER' : 'VENDOR'}</th>
                <th className="text-left p-2.5 font-semibold text-stone-600">PHONE</th>
                <th className="text-right p-2.5 font-semibold text-stone-600">{activeTab === 'cash_in' ? 'BILLED' : 'OWED'}</th>
                {activeTab === 'cash_out' && <th className="text-right p-2.5 font-semibold text-stone-600">EXTRA</th>}
                <th className="text-right p-2.5 font-semibold text-stone-600">PAID</th>
                <th className="text-right p-2.5 font-semibold text-stone-600">OUTSTANDING</th>
              </tr>
            </thead>
            <tbody>
              {outstandingList.map((item) => {
                const isVendor = activeTab === 'cash_out';
                const rowId = item.customer_id || item.vendor_id;
                const expanded = isVendor && expandedVendor === item.vendor_id;
                const stmt = isVendor ? vendorStatements[item.vendor_id] : null;
                const colSpan = isVendor ? 6 : 5;
                return (
                <React.Fragment key={rowId}>
                <tr data-testid={`outstanding-${rowId}`} className={`border-b border-stone-100 hover:bg-stone-50 ${isVendor ? 'cursor-pointer' : ''}`} onClick={isVendor ? () => toggleVendorStatement(item.vendor_id) : undefined}>
                  <td className="p-2.5 font-medium text-stone-900">
                    <div className="flex items-center gap-1.5">
                      {isVendor && (
                        statementLoading === item.vendor_id
                          ? <Loader2 className="w-3 h-3 animate-spin text-stone-400" />
                          : (expanded ? <ChevronDown className="w-3.5 h-3.5 text-stone-500" /> : <ChevronRight className="w-3.5 h-3.5 text-stone-500" />)
                      )}
                      <span>{item.customer_name || item.vendor_name}</span>
                    </div>
                  </td>
                  <td className="p-2.5 text-stone-600">{item.phone || '-'}</td>
                  <td className="p-2.5 text-right tabular-nums text-stone-700">
                    &#8377;{formatPrice(activeTab === 'cash_in' ? (item.billed || 0) : (item.product_owed || item.owed || 0))}
                  </td>
                  {isVendor && (
                    <td className="p-2.5 text-right tabular-nums text-amber-700">
                      {(item.extra_charges || 0) > 0 ? `₹${formatPrice(item.extra_charges)}` : '-'}
                    </td>
                  )}
                  <td className="p-2.5 text-right tabular-nums text-lime-700 font-medium">&#8377;{formatPrice(item.paid)}</td>
                  <td className="p-2.5 text-right tabular-nums font-bold">
                    <span className={item.outstanding > 0 ? 'text-red-700' : 'text-lime-700'}>&#8377;{formatPrice(item.outstanding)}</span>
                  </td>
                </tr>
                {expanded && stmt && (
                  <tr data-testid={`vendor-statement-${item.vendor_id}`} className="bg-stone-50/60">
                    <td colSpan={colSpan} className="p-0">
                      <VendorStatement statement={stmt} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Payment History */}
      <div className="flex gap-2 items-center">
        <span className="text-xs text-stone-600 font-medium">Payment History</span>
        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger className="w-40 border-stone-200 text-xs h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" className="text-xs">All Methods</SelectItem>
            {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-stone-500 ml-auto">{filteredPayments.length} records &middot; &#8377;{formatPrice(tabTotal)}</span>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-stone-100 border-b border-stone-300">
            <tr>
              <th className="text-left p-2.5 font-semibold text-stone-700">DATE</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">{activeTab === 'cash_in' ? 'CUSTOMER' : 'VENDOR'}</th>
              <th className="text-right p-2.5 font-semibold text-stone-700">AMOUNT</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">METHOD</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">REF #</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">NOTES</th>
              <th className="text-right p-2.5 font-semibold text-stone-700 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.length === 0 ? (
              <tr><td colSpan="7" className="p-8 text-center text-stone-500">No payments recorded yet.</td></tr>
            ) : (
              filteredPayments.map((pay, idx) => (
                <tr key={pay.payment_id} data-testid={`payment-row-${pay.payment_id}`} className={`border-b border-stone-100 hover:bg-stone-50 ${idx % 2 === 0 ? '' : 'bg-stone-50/50'}`}>
                  <td className="p-2.5 tabular-nums text-stone-700">{pay.date}</td>
                  <td className="p-2.5 font-medium text-stone-900">{getEntityName(pay)}</td>
                  <td className="p-2.5 text-right font-semibold tabular-nums">
                    <span className={pay.type === 'cash_in' ? 'text-lime-700' : 'text-red-700'}>&#8377;{formatPrice(pay.amount)}</span>
                  </td>
                  <td className="p-2.5">{methodBadge(pay.payment_method)}</td>
                  <td className="p-2.5 text-stone-600 font-mono">{pay.reference_number || '-'}</td>
                  <td className="p-2.5 text-stone-600">{pay.notes || '-'}</td>
                  <td className="p-2.5 text-right">
                    <button data-testid={`delete-payment-${pay.payment_id}`} onClick={() => handleDelete(pay.payment_id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Record Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{formData.type === 'cash_in' ? 'Record Cash-In (from Customer)' : 'Record Cash-Out (to Vendor)'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label className="text-xs">{formData.type === 'cash_in' ? 'Customer' : 'Vendor'} *</Label>
              <Select value={formData.entity_id} onValueChange={(v) => setFormData({ ...formData, entity_id: v })}>
                <SelectTrigger data-testid="payment-entity-select" className="border-stone-200 h-9"><SelectValue placeholder={`Select ${formData.type === 'cash_in' ? 'customer' : 'vendor'}`} /></SelectTrigger>
                <SelectContent>
                  {formData.type === 'cash_in'
                    ? customers.map(c => {
                        const rec = receivables.find(r => r.customer_id === c.customer_id);
                        const ost = rec ? rec.outstanding : 0;
                        return <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}{ost > 0 ? ` (Due: ₹${formatPrice(ost)})` : ''}</SelectItem>;
                      })
                    : vendors.map(v => {
                        const pay = payables.find(p => p.vendor_id === v.vendor_id);
                        const ost = pay ? pay.outstanding : 0;
                        return <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}{ost > 0 ? ` (Due: ₹${formatPrice(ost)})` : ''}</SelectItem>;
                      })
                  }
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount Received (cash / UPI / bank) *</Label>
              <Input data-testid="payment-amount-input" type="number" step="0.01" min="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" required className="border-stone-200 h-9" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Discount</Label>
                <Input data-testid="payment-discount-input" type="number" step="0.01" min="0" value={formData.discount} onChange={(e) => setFormData({ ...formData, discount: e.target.value })} placeholder="0.00" className="border-stone-200 h-9" />
              </div>
              <div>
                <Label className="text-xs">Round-off (±)</Label>
                <Input data-testid="payment-roundoff-input" type="number" step="0.01" value={formData.round_off} onChange={(e) => setFormData({ ...formData, round_off: e.target.value })} placeholder="0.00" className="border-stone-200 h-9" />
              </div>
            </div>
            {(parseFloat(formData.amount) > 0 || parseFloat(formData.discount) > 0 || parseFloat(formData.round_off) !== 0) && (
              <div className="bg-stone-50 border border-stone-200 rounded px-2 py-1.5 text-[11px] text-stone-700">
                Closes balance by <strong>₹{formatPrice((parseFloat(formData.amount) || 0) + (parseFloat(formData.discount) || 0) + (parseFloat(formData.round_off) || 0))}</strong>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Method *</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger data-testid="payment-method-select" className="border-stone-200 h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="border-stone-200 h-9" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Reference #</Label>
                <Input data-testid="payment-ref-input" value={formData.reference_number} onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} placeholder="Txn / Cheque" className="border-stone-200 h-9" />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional" className="border-stone-200 h-9" />
              </div>
            </div>
            <Button type="submit" data-testid="submit-payment-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">
              {formData.type === 'cash_in' ? 'Record Cash-In' : 'Record Cash-Out'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;
